// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@rpsc/App/Components/Common/XngZone";
import Button, { ButtonPresentationProps } from "@rpsc/MobiusOverrides/Button/Button";
import LazyImage, { LazyImageProps } from "@rpsc/MobiusOverrides/LazyImage";
import LazyPicture from "@rpsc/MobiusOverrides/LazyImage/LazyPicture";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import { xParserOptions } from "@rpsc/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";

interface XngBannerStyles {
    container?: InjectableCss;
    backgroundImage?: LazyImageProps;
    imageContent?: LazyImageProps;
    contentDetailsWrapper?: InjectableCss;
    title?: TypographyProps;
    subTitle?: TypographyProps;
    description?: TypographyProps;
    button?: ButtonPresentationProps;
}

export const xngBannerStyles: XngBannerStyles = {};

const styles = xngBannerStyles;

const enum sectionFields {
    largeBackgroundImage = "largeBackgroundImage",
    mediumBackgroundImage = "mediumBackgroundImage",
    smallBackgroundImage = "smallBackgroundImage",
    backgroundColor = "backgroundColor",
    imageContent = "imageContent",
    imageContentPosition = "imageContentPosition",
    title = "title",
    subTitle = "subTitle",
    description = "description",
    buttonTitle = "buttonTitle",
    buttonLink = "buttonLink",
    openInNewTab = "openInNewTab",
    reverseContainer = "reverseContainer",
    containerPaddingForLargeScreens = "containerPaddingForLargeScreens",
    containerPaddingForSmallScreens = "containerPaddingForSmallScreens",
    containerGapForSmallScreens = "containerGapForSmallScreens",
    contentPaddingForLargeScreens = "contentPaddingForLargeScreens",
    contentPaddingForSmallScreens = "contentPaddingForSmallScreens",
    contentMarginForLargeScreens = "contentMarginForLargeScreens",
    contentMarginForSmallScreens = "contentMarginForSmallScreens",
    maxImageContentHeight = "maxImageContentHeight",
    contentBackgroundColor = "contentBackgroundColor",
    contentBorderRadius = "contentBorderRadius",
    imgBorderRadius = "imgBorderRadius",
    textContentMaxWidth = "textContentMaxWidth",
    contentAlignmentLargeScreens = "contentAlignmentLargeScreens",
    contentAlignmentSmallScreens = "contentAlignmentSmallScreens",
    backgroundAltText = "backgroundAltText",
    contentAltText = "contentAltText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [sectionFields.largeBackgroundImage]: string;
        [sectionFields.mediumBackgroundImage]: string;
        [sectionFields.smallBackgroundImage]: string;
        [sectionFields.backgroundColor]: string;
        [sectionFields.imageContent]: string;
        [sectionFields.imageContentPosition]: "right" | "left";
        [sectionFields.title]: string;
        [sectionFields.subTitle]: string;
        [sectionFields.description]: string;
        [sectionFields.buttonTitle]: string;
        [sectionFields.buttonLink]: LinkFieldValue | string;
        [sectionFields.openInNewTab]: boolean;
        [sectionFields.reverseContainer]: boolean;
        [sectionFields.containerPaddingForLargeScreens]: string;
        [sectionFields.containerPaddingForSmallScreens]: string;
        [sectionFields.containerGapForSmallScreens]: string;
        [sectionFields.contentPaddingForLargeScreens]: string;
        [sectionFields.contentPaddingForSmallScreens]: string;
        [sectionFields.contentMarginForLargeScreens]: string;
        [sectionFields.contentMarginForSmallScreens]: string;
        [sectionFields.maxImageContentHeight]: string;
        [sectionFields.contentBackgroundColor]: string;
        [sectionFields.contentBorderRadius]: string;
        [sectionFields.imgBorderRadius]: string;
        [sectionFields.textContentMaxWidth]: string;
        [sectionFields.contentAlignmentLargeScreens]: string;
        [sectionFields.contentAlignmentSmallScreens]: string;
        [sectionFields.backgroundAltText]: string;
        [sectionFields.contentAltText]: string;
    };
    imageKey: string;
}
type Props = OwnProps;

const XngBanner: FC<Props> = ({
    fields: {
        largeBackgroundImage,
        mediumBackgroundImage,
        smallBackgroundImage,
        backgroundColor,
        imageContent,
        imageContentPosition,
        reverseContainer,
        title,
        subTitle,
        description,
        buttonTitle,
        buttonLink,
        openInNewTab,
        containerPaddingForLargeScreens,
        containerPaddingForSmallScreens,
        containerGapForSmallScreens,
        contentPaddingForLargeScreens,
        contentPaddingForSmallScreens,
        contentMarginForLargeScreens,
        contentMarginForSmallScreens,
        maxImageContentHeight,
        contentBackgroundColor,
        contentBorderRadius,
        imgBorderRadius,
        textContentMaxWidth,
        contentAlignmentLargeScreens,
        contentAlignmentSmallScreens,
        backgroundAltText,
        contentAltText,
    },
    id,
    imageKey,
}) => {
    const _buttonLink = typeof buttonLink === "string" ? buttonLink : useGetLink(buttonLink).url;
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!_buttonLink) {
            return;
        }

        if (_buttonLink.startsWith("http")) {
            if (isInShell) {
                return;
            }
            openInNewTab ? window.open(_buttonLink) : (window.location.href = _buttonLink);
        } else {
            openInNewTab ? window.open(_buttonLink) : history.push(_buttonLink);
        }
    };

    const BannerContent = (<StyledWrapper
        {...styles.contentDetailsWrapper}
        {...{
            contentPaddingForLargeScreens,
            contentPaddingForSmallScreens,
            contentMarginForLargeScreens,
            contentMarginForSmallScreens,
            contentBackgroundColor,
            contentBorderRadius,
            textContentMaxWidth,
            contentAlignmentLargeScreens,
            contentAlignmentSmallScreens,
            imageContent,
        }}
    >
        {title && <Typography {...styles.title}>{parse(title, xParserOptions)}</Typography>}
        {subTitle && <Typography {...styles.subTitle}>{parse(subTitle, xParserOptions)}</Typography>}
        {description && (
            <Typography {...styles.description}>{parse(description, xParserOptions)}</Typography>
        )}
        {!!buttonTitle && !!_buttonLink && (
            <Button {...styles.button} onClick={onClick} aria-label={buttonTitle || _buttonLink}>
                {buttonTitle}
            </Button>
        )}
        <XngZone zoneName="content" contentId={id} hint="Content" />
    </StyledWrapper>)

    return (
        <StyledWrapper
            {...styles.container}
            {...{
                backgroundColor,
                reverseContainer,
                containerPaddingForLargeScreens,
                containerPaddingForSmallScreens,
                containerGapForSmallScreens,
                imgBorderRadius,
                maxImageContentHeight,
                imageContent,
            }}
            className="banner-container"
        >
            {(largeBackgroundImage || mediumBackgroundImage || smallBackgroundImage) && (
                <LazyPicture
                    {...styles.backgroundImage}
                    largeImageSrc={largeBackgroundImage || mediumBackgroundImage}
                    mediumImageSrc={mediumBackgroundImage || largeBackgroundImage}
                    smallImageSrc={smallBackgroundImage || mediumBackgroundImage}
                    altText={backgroundAltText || "Background Image"}
                    {...{ maxImageContentHeight }}
                />
            )}
            {imageContent && (
                <LazyImage
                    src={imageContent}
                    {...styles.imageContent}
                    {...{ maxImageContentHeight, imageContentPosition }}
                    {...{ imageKey, altText: contentAltText || "Banner Text" }}
                />
            )}
            {imageContent ? BannerContent : <StyledWrapper className="banner-content">
                {BannerContent}
            </StyledWrapper>}
        </StyledWrapper>
    );
};

const customizationsTab = {
    displayName: "Customizations",
    sortOrder: 0,
};

const contentTab = {
    displayName: "Contents",
    sortOrder: 1,
};

const alignmentOptions = [
    { value: "flex-start", displayName: "Start" },
    { value: "center", displayName: "Center" },
    { value: "flex-end", displayName: "End" },
];

const xngBannerWidget: WidgetModule = {
    component: XngBanner,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                fieldType: "General",
                name: sectionFields.largeBackgroundImage,
                displayName: "Large Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: customizationsTab,
            },
            {
                fieldType: "General",
                name: sectionFields.mediumBackgroundImage,
                displayName: "Medium Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: customizationsTab,
            },
            {
                fieldType: "General",
                name: sectionFields.smallBackgroundImage,
                displayName: "Small Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: customizationsTab,
            },
            {
                fieldType: "General",
                name: sectionFields.backgroundAltText,
                displayName: "Background Image Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: sectionFields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "",
                tab: customizationsTab,
            },
            {
                fieldType: "General",
                name: sectionFields.imageContent,
                displayName: "Content Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: sectionFields.contentAltText,
                displayName: "Content Image Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "Contextual",
                name: sectionFields.imageContentPosition,
                displayName: "image position",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Right", value: "right" },
                    { displayName: "Left", value: "left" },
                ],
                defaultValue: "left",
                tab: contentTab,
            },
            {
                name: sectionFields.reverseContainer,
                displayName: "Reverse Container Contents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customizationsTab,
            },
            {
                fieldType: "General",
                name: sectionFields.title,
                editorTemplate: "RichTextField",
                displayName: "Title",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: sectionFields.subTitle,
                editorTemplate: "RichTextField",
                displayName: "Sub Title",
                defaultValue: "",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: sectionFields.description,
                editorTemplate: "RichTextField",
                displayName: "Description",
                defaultValue: "",
                tab: contentTab,
            },
            {
                editorTemplate: "TextField",
                name: sectionFields.buttonTitle,
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
            },
            {
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Page",
                    value: "",
                },
                name: sectionFields.buttonLink,
                fieldType: "General",
                tab: contentTab,
            },
            {
                name: sectionFields.openInNewTab,
                displayName: "Open In New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => item.fields.buttonTitle && item.fields.buttonLink,
                tab: contentTab,
            },
            {
                name: sectionFields.containerPaddingForLargeScreens,
                displayName: "Container Padding For Large Screens",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "32px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                tab: customizationsTab,
            },
            {
                name: sectionFields.containerPaddingForSmallScreens,
                displayName: "Container Padding For Small Screens",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "16px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                tab: customizationsTab,
            },
            {
                name: sectionFields.containerGapForSmallScreens,
                displayName: "Container Gap For Small Screens",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "16px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("gap", value) ? null : `Invalid Input`;
                },
                tab: customizationsTab,
            },
            {
                name: sectionFields.contentPaddingForLargeScreens,
                displayName: "Content Padding For Large Screens",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                tab: contentTab,
            },
            {
                name: sectionFields.contentPaddingForSmallScreens,
                displayName: "Content Padding For Small Screens",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("padding", value) ? null : `Invalid Input`;
                },
                tab: contentTab,
            },
            {
                name: sectionFields.contentMarginForLargeScreens,
                displayName: "Content Margin For Large Screens",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
                tab: contentTab,
            },
            {
                name: sectionFields.contentMarginForSmallScreens,
                displayName: "Content Margin For Small Screens",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
                tab: contentTab,
            },
            {
                name: sectionFields.maxImageContentHeight,
                displayName: "Maximum Image Content Height",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "283px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("height", value) ? null : `Invalid Input`;
                },
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: sectionFields.contentBackgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Content Background Color",
                defaultValue: "",
                tab: contentTab,
            },
            {
                name: sectionFields.contentBorderRadius,
                displayName: "Content Border Radius",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("border-radius", value) ? null : `Invalid Input`;
                },
                tab: contentTab,
            },
            {
                name: sectionFields.imgBorderRadius,
                displayName: "Image Border Radius",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "2px",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("border-radius", value) ? null : `Invalid Input`;
                },
                tab: contentTab,
            },
            {
                name: sectionFields.textContentMaxWidth,
                displayName: "Text Content Max Width",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "100%",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("width", value) ? null : `Invalid Input`;
                },
                tab: contentTab,
            },

            {
                name: sectionFields.contentAlignmentLargeScreens,
                displayName: "Content Alignment Large Screens",
                editorTemplate: "DropDownField",
                options: alignmentOptions,
                defaultValue: "center",
                fieldType: "Contextual",
                tab: contentTab,
            },
            {
                name: sectionFields.contentAlignmentSmallScreens,
                displayName: "Content Alignment Small Screens",
                editorTemplate: "DropDownField",
                options: alignmentOptions,
                defaultValue: "center",
                fieldType: "Contextual",
                tab: contentTab,
            },
        ],
    },
};
export default xngBannerWidget;
